import React, { useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, Button, Input, Divider, Badge } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useMediaQuery } from '../hooks';

const Section = ({ title, children }) => (
  <Card style={{ padding: '24px' }}>
    <p style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 20 }}>{title}</p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>{children}</div>
  </Card>
);

const InfoRow = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)', fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>{value}</span>
  </div>
);

export const SettingsPage = () => {
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [pwForm, setPwForm]   = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwLoading, setPwLoading] = useState(false);

  const setPw = (k) => (e) => setPwForm((f) => ({ ...f, [k]: e.target.value }));

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!pwForm.currentPassword)                  errs.currentPassword = 'Required';
    if (!pwForm.newPassword || pwForm.newPassword.length < 8) errs.newPassword = 'Min 8 characters';
    if (pwForm.newPassword !== pwForm.confirm)     errs.confirm = 'Passwords do not match';
    setPwErrors(errs);
    if (Object.keys(errs).length) return;

    setPwLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Password updated. Please log in again.');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update password');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <AppLayout>
      <PageHeader title="Settings" subtitle="Manage your account and preferences" />
      <div style={{ padding: isMobile ? '16px' : '24px 32px', maxWidth: 680, display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Profile */}
        <Section title="Account">
          <InfoRow label="Name"   value={user?.name} />
          <InfoRow label="Email"  value={user?.email} mono />
          <InfoRow label="Plan"   value={<Badge variant={user?.plan === 'pro' ? 'cyan' : 'default'}>{user?.plan || 'free'}</Badge>} />
          <InfoRow label="Emails Sent" value={user?.emailsCount ?? 0} mono />
          <InfoRow label="Member Since" value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'} mono />
        </Section>

        {/* Change Password */}
        <Section title="Change Password">
          <Input label="Current Password" type="password" value={pwForm.currentPassword} onChange={setPw('currentPassword')} error={pwErrors.currentPassword} placeholder="Current password" />
          <Input label="New Password"     type="password" value={pwForm.newPassword}     onChange={setPw('newPassword')}     error={pwErrors.newPassword}     placeholder="Min 8 characters" />
          <Input label="Confirm Password" type="password" value={pwForm.confirm}         onChange={setPw('confirm')}         error={pwErrors.confirm}         placeholder="Repeat new password" />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleChangePassword} loading={pwLoading} size="sm">Update Password</Button>
          </div>
        </Section>

        {/* Tracking Settings (informational) */}
        <Section title="Tracking Configuration">
          {[
            { label: 'Open Tracking (Pixel)',   value: 'Enabled', color: 'var(--green)' },
            { label: 'Link Click Tracking',     value: 'Enabled', color: 'var(--green)' },
            { label: 'Bot Filtering',           value: 'Enabled', color: 'var(--green)' },
            { label: 'Geo-IP Resolution',       value: 'Enabled', color: 'var(--green)' },
            { label: 'Real-time Notifications', value: 'WebSocket', color: 'var(--cyan)' },
            { label: 'Event Queue',             value: process.env.REACT_APP_KAFKA_ENABLED === 'true' ? 'Kafka' : 'Direct', color: 'var(--text-secondary)' },
          ].map((item) => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{item.label}</span>
              <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color: item.color }}>{item.value}</span>
            </div>
          ))}
        </Section>

        {/* Privacy */}
        <Section title="Privacy & Compliance">
          <div style={{ padding: '12px 16px', background: 'var(--cyan-dim)', border: '1px solid rgba(0,225,200,0.2)', borderRadius: 'var(--radius-md)', fontSize: '0.78rem', color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>
            ✓ GDPR-aware design — contact support to request data export or deletion
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="danger" size="sm" onClick={() => toast('Contact support@emailtracker.io to delete your account', { icon: 'ℹ️' })}>
              Request Account Deletion
            </Button>
          </div>
        </Section>

      </div>
    </AppLayout>
  );
};
